import React from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

/**
 * BOF-76 — every reason a figure on the dashboard is absent, said in words.
 *
 * The rule this component exists to enforce: an absence is never rendered as a zero. A month that was
 * never calculated, a day before the charge-point snapshot existed, or a period computed before power
 * types were known would all look like "nothing happened" if drawn as 0 — and a reader would believe it.
 */
export type UnavailableReason =
  | { kind: "neverComputed" }
  | { kind: "censusNotRecorded" }
  | { kind: "splitUnavailable" }
  | { kind: "incomplete"; elapsed: string }
  | { kind: "partial"; missingDays: string[] };

interface UnavailableNoticeProps {
  reason: UnavailableReason;
  /** Shown for `neverComputed`: the recompute route that already exists on this page. */
  onRecompute?: () => void;
  /** Compact form for use inside a panel, rather than across the page. */
  inline?: boolean;
}

const MAX_LISTED_DAYS = 5;

const describe = (reason: UnavailableReason): { severity: "info" | "warn"; text: string } => {
  switch (reason.kind) {
    case "neverComputed":
      return {
        severity: "warn",
        text: "This period has not been calculated yet. This is not the same as no activity — run a recompute to fill it.",
      };
    case "censusNotRecorded":
      return {
        severity: "info",
        text: "Charge-point counts were not recorded for this period. They are snapshotted daily and cannot be reconstructed for days before snapshots began.",
      };
    case "splitUnavailable":
      return {
        severity: "info",
        text: "The AC/DC split is not available for this period: it was calculated before connector power types were known. Totals are unaffected.",
      };
    case "incomplete":
      return {
        severity: "info",
        text: `This period is still running — figures cover ${reason.elapsed} and are not projected to a full period.`,
      };
    case "partial": {
      const shown = reason.missingDays.slice(0, MAX_LISTED_DAYS).join(", ");
      const more = reason.missingDays.length - MAX_LISTED_DAYS;
      return {
        severity: "warn",
        text:
          `Totals are understated: ${reason.missingDays.length} day(s) in this period have no data (${shown}` +
          `${more > 0 ? ` and ${more} more` : ""}).`,
      };
    }
  }
};

const UnavailableNotice: React.FC<UnavailableNoticeProps> = ({ reason, onRecompute, inline = false }) => {
  const { severity, text } = describe(reason);
  const action =
    reason.kind === "neverComputed" && onRecompute ? (
      <Button label="Open recompute" icon="pi pi-database" size="small" text onClick={onRecompute} className="ml-2" />
    ) : null;

  return (
    <Message
      severity={severity}
      className={inline ? "w-full justify-content-start text-sm" : "w-full justify-content-start mb-3"}
      content={
        <div className="flex align-items-center flex-wrap gap-2">
          <i className={`pi ${severity === "warn" ? "pi-exclamation-triangle" : "pi-info-circle"}`} />
          <span>{text}</span>
          {action}
        </div>
      }
    />
  );
};

export default UnavailableNotice;
